import { cn } from "@choose/ui";
import { Button } from "@choose/ui/button";
import { Dialog, DialogContent } from "@choose/ui/dialog";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from "@choose/ui/dropdown-menu";
import {
  Empty,
  EmptyDescription,
  EmptyHeader,
  EmptyMedia,
  EmptyTitle,
} from "@choose/ui/empty";
import { Separator } from "@choose/ui/separator";
import { Spinner } from "@choose/ui/spinner";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@choose/ui/tabs";
import { createFileRoute, Link } from "@tanstack/react-router";
import {
  BookSearchIcon,
  CandyOffIcon,
  EllipsisIcon,
  PanelsTopLeftIcon,
  ServerCrashIcon,
  SquarePenIcon,
} from "lucide-react";
import { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";

import { AddressView } from "~/components/common/order/address-view";
import { AddressViewFallback } from "~/components/common/order/address-view-fallback";
import { CashbackStatus } from "~/components/common/order/cashback/cashback-status";
import { OrderItemWithPrice } from "~/components/common/order/order-item-with-price";
import { CreateTicketDialog } from "~/components/common/ticket/create-ticket-dialog";
import {
  DEFAULT_COLUMN_VISIBILITY,
  TABLE_ACCESSOR_ORDER,
  TABLE_ACCESSOR_SUB_STATUS,
  TicketList,
} from "~/components/common/tickets/ticket-list";
import { OrderParcels } from "~/components/layout/sidebars/ticket-detail-sidebar/order-parcels";
import { PickupPointOpeningHours } from "~/components/layout/sidebars/ticket-detail-sidebar/pickup-point-opening-hours";
import { Copy } from "~/components/ui/copy";
import {
  SupportTicketsSortableDirection,
  SupportTicketsSortableFields,
  useCurrentUserQuery,
  useInfiniteTicketsQuery,
  useOrderQuery,
} from "~/graphql/graphql.gen";
import { useFormatAddressQuery } from "~/hooks/use-format-address-query";
import { formatDate } from "~/libs/date";
import { toLocale } from "~/libs/locale";
import { formatCurrency } from "~/libs/number";
import { getOrderBrandDashboardUrl } from "~/libs/url";
import { getCashbackStatuses } from "~/stores/cashback-statuses-store";
import { getContactFullName } from "~/stores/contacts-store";
import {
  getOrderDeliveryMethod,
  getOrderDeliveryMethods,
  getReturnStatus,
  groupItems,
} from "~/stores/orders-store";
import { countFilters, useQueueStore } from "~/stores/queue-store";
import { AGENT_ROLES } from "~/stores/roles-store";

export const Route = createFileRoute(
  "/_auth/_app_shell/_main-navigation/order/$orderId",
)({
  component: RouteComponent,
});

function RouteComponent() {
  const { i18n, t } = useTranslation();
  const [isOpen, setIsOpen] = useState(false);
  const { setQueue } = useQueueStore();
  const { orderId } = Route.useParams();
  const { data: user } = useCurrentUserQuery(undefined, {
    select: (data) => data.currentUser,
  });
  const {
    data: order,
    isLoading,
    error,
  } = useOrderQuery(
    {
      input: {
        orderId: orderId,
      },
    },
    {
      select: (data) => data.order,
    },
  );

  const { data: recipientFormattedAddress } = useFormatAddressQuery({
    address: {
      firstName: order?.recipient.firstName,
      lastName: order?.recipient.lastName,
      address1: order?.recipient.address.street ?? "",
      address2: order?.recipient.address.streetAdditional ?? "",
      city: order?.recipient.address.city ?? "",
      country: order?.recipient.address.countryCode ?? "",
      province: order?.recipient.address.state ?? undefined,
      zip: order?.recipient.address.zipCode ?? "",
    },
    locale: toLocale(i18n.language),
  });
  const { data: pickupPointFormattedAddress } = useFormatAddressQuery({
    address: {
      company: order?.shipping.pickupPoint?.address?.name ?? "",
      address1: order?.shipping.pickupPoint?.address?.street ?? "",
      address2: order?.shipping.pickupPoint?.address?.streetAdditional ?? "",
      city: order?.shipping.pickupPoint?.address?.city ?? "",
      country: order?.shipping.pickupPoint?.address?.countryCode ?? "FR",
      zip: order?.shipping.pickupPoint?.address?.zipCode ?? "",
    },
    locale: toLocale(i18n.language),
  });

  useEffect(() => {
    const filters = [
      {
        id: TABLE_ACCESSOR_ORDER,
        value: [orderId],
      },
    ];

    const sorting = [
      {
        id: TABLE_ACCESSOR_SUB_STATUS,
        desc: false,
      },
    ];

    setQueue({
      display: {
        filterCount: countFilters({
          currentFilters: filters,
          referenceFilters: [],
        }),
        subTitle: t("ticket.status.todo"),
        title: t("app_sidebar.tickets"),
      },
      root: {
        page: {
          to: Route.fullPath,
          params: { orderId },
        },
      },
      state: {
        columnFilters: filters,
        sorting,
      },
    });
  }, [orderId, setQueue, t]);

  if (isLoading) {
    return (
      <>
        <title>{t("app_name")}</title>

        <main className="size-full overflow-hidden md:p-2">
          <div className="bg-container flex size-full overflow-hidden md:rounded-md md:border">
            <div className="bg-background flex size-full flex-col items-center justify-center">
              <Empty className="min-h-[300px] w-full max-w-md">
                <EmptyHeader>
                  <EmptyMedia variant="icon">
                    <Spinner className="size-4" />
                  </EmptyMedia>
                  <EmptyTitle>{t("order.loading.title")}</EmptyTitle>
                  <EmptyDescription>
                    {t("order.loading.description")}
                  </EmptyDescription>
                </EmptyHeader>
              </Empty>
            </div>
          </div>
        </main>
      </>
    );
  }

  if (!order || (error as Error | undefined)?.message === "order_not_found")
    return (
      <>
        <title>{`${t("order.not_found.title")} - ${t("app_name")}`}</title>

        <main className="size-full overflow-hidden md:p-2">
          <div className="bg-container flex size-full overflow-hidden md:rounded-md md:border">
            <div className="bg-background flex size-full justify-center overflow-hidden">
              <Empty>
                <EmptyHeader>
                  <EmptyMedia variant="icon">
                    <CandyOffIcon />
                  </EmptyMedia>
                  <EmptyTitle>{t("order.not_found.title")}</EmptyTitle>
                  <EmptyDescription>
                    {t("order.not_found.description")}
                  </EmptyDescription>
                </EmptyHeader>
              </Empty>
            </div>
          </div>
        </main>
      </>
    );

  if (error) {
    return (
      <>
        <title>{t("app_name")}</title>

        <main className="size-full overflow-hidden md:p-2">
          <div className="bg-container flex size-full overflow-hidden md:rounded-md md:border">
            <div className="bg-background flex size-full flex-col justify-center">
              <Empty>
                <EmptyHeader>
                  <EmptyMedia variant="icon">
                    <ServerCrashIcon />
                  </EmptyMedia>
                  <EmptyTitle>{t("order.error")}</EmptyTitle>
                </EmptyHeader>
              </Empty>
            </div>
          </div>
        </main>
      </>
    );
  }

  const hasRights =
    user?.__typename === "ActorInternalUser" &&
    AGENT_ROLES.includes(user.internalUser.supportRole);

  const orderItems = order.items.map((item) => ({
    ...item,
    returnStatus: getReturnStatus(item, order.returns),
  }));

  const orderDeliveryMethod = getOrderDeliveryMethods(t).find(
    (method) =>
      method.value === getOrderDeliveryMethod(order.shipping.pickupPoint),
  );

  const groupedItems = groupItems(orderItems);

  return (
    <>
      <title>{`${order.id} - ${t("app_name")}`}</title>

      <div className="flex w-full flex-col md:flex-row">
        <div className="bg-background mb-1 w-full shrink-0 overflow-auto rounded-lg border px-4 py-6 md:mr-1 md:mb-0 md:w-[356px]">
          <div className="flex items-center justify-between">
            <span>{order.id}</span>
            <DropdownMenu>
              <DropdownMenuTrigger
                render={
                  <Button variant="ghost" size="sm">
                    <EllipsisIcon />
                  </Button>
                }
              />
              <DropdownMenuContent align="end" className="min-w-3xs">
                <DropdownMenuItem
                  className="gap-2"
                  onClick={() => setIsOpen(true)}
                  disabled={!hasRights}
                >
                  <SquarePenIcon className="text-muted-foreground size-4" />
                  <span>{t("create_ticket_dialog.title")}</span>
                </DropdownMenuItem>
              </DropdownMenuContent>
            </DropdownMenu>
          </div>
          <div className="-mx-3 pt-4">
            <Button
              variant="link"
              size="sm"
              nativeButton={false}
              render={
                <a
                  href={getOrderBrandDashboardUrl({
                    brandId: order.brand.id,
                    orderId: order.id,
                    saleId: order.sale.id,
                  })}
                  target="_blank"
                  rel="noreferrer"
                >
                  <PanelsTopLeftIcon /> Brand
                </a>
              }
            />
          </div>

          <div className="my-2">
            <Separator />
          </div>

          <div className="text-sm">
            <div className="flex items-center">
              <Copy
                content={order.sale.name}
                className="text-muted-foreground shrink-0 basis-[92px] truncate text-left text-sm"
              >
                {t("order.sale")}
              </Copy>
              <div className="flex min-h-8 items-center">
                <Button
                  type="button"
                  variant="ghost"
                  size="sm"
                  nativeButton={false}
                  render={
                    <Link
                      to={`/sale/$saleId`}
                      params={{ saleId: order.sale.id }}
                    >
                      {order.sale.name}
                    </Link>
                  }
                />
              </div>
            </div>
            <div className="flex items-center">
              <Copy
                content={getContactFullName(order.supportActorAppContact)}
                className="text-muted-foreground shrink-0 basis-[92px] truncate text-left text-sm"
              >
                {t("order.contact")}
              </Copy>
              <div className="flex min-h-8 items-center">
                <Button
                  type="button"
                  variant="ghost"
                  size="sm"
                  nativeButton={false}
                  render={
                    <Link
                      to={`/contact/$contactId`}
                      params={{ contactId: order.supportActorAppContact.id }}
                    >
                      {getContactFullName(order.supportActorAppContact)}
                    </Link>
                  }
                />
              </div>
            </div>

            <div className="my-2">
              <Separator />
            </div>

            <div className="flex items-center">
              <Copy
                content={formatCurrency(
                  order.totalPriceSold.valueWithVat / 100,
                  toLocale(i18n.language),
                  { currency: order.totalPriceSold.currency },
                )}
                className="text-muted-foreground shrink-0 basis-[92px] truncate text-left text-sm"
              >
                {t("ticket.order_detail.total_amount")}
              </Copy>
              <div
                className={cn(
                  "flex min-h-8 items-center gap-1 overflow-hidden px-3",
                  {
                    "line-through": order.isCancelled,
                  },
                )}
              >
                <span>
                  {formatCurrency(
                    order.totalPriceSold.valueWithVat / 100,
                    toLocale(i18n.language),
                    { currency: order.totalPriceSold.currency },
                  )}
                </span>
              </div>
            </div>
            {order.shipping.fees ? (
              <div className="flex items-center">
                <Copy
                  content={formatCurrency(
                    order.shipping.feesSold / 100,
                    toLocale(i18n.language),
                    { currency: order.totalPriceSold.currency },
                  )}
                  className="text-muted-foreground shrink-0 basis-[92px] truncate text-left text-sm"
                >
                  {t("ticket.order_detail.shipping_fees")}
                </Copy>
                <div
                  className={cn(
                    "flex min-h-8 items-center gap-1 overflow-hidden px-3",
                    {
                      "line-through": order.isCancelled,
                    },
                  )}
                >
                  <span className="truncate">
                    {order.shipping.feesSold
                      ? formatCurrency(
                          order.shipping.feesSold / 100,
                          toLocale(i18n.language),
                          { currency: order.totalPriceSold.currency },
                        )
                      : t("order.shipping.fees.offered")}
                  </span>
                  {order.shipping.feesSold &&
                  order.shipping.refundSummary.refundedAmountInCents > 0 ? (
                    <span className="text-yellow-600">
                      (
                      {order.shipping.refundSummary.isTotallyRefunded
                        ? t("order.shipping.fees.refunded")
                        : t("order.shipping.fees.x_refunded", {
                            amount: formatCurrency(
                              order.shipping.refundSummary
                                .refundedAmountInCents / 100,
                              toLocale(i18n.language),
                              { currency: order.totalPriceSold.currency },
                            ),
                          })}
                      )
                    </span>
                  ) : null}
                </div>
              </div>
            ) : null}
            <div className="flex items-center">
              <Copy
                content={new Date(order.createdAt).toUTCString()}
                className="text-muted-foreground shrink-0 basis-[92px] truncate text-left text-sm"
              >
                {t("ticket.order_detail.created_at")}
              </Copy>
              <div className="flex min-h-8 items-center px-3">
                {formatDate(
                  new Date(order.createdAt),
                  toLocale(i18n.language),
                  {
                    dateStyle: "medium",
                    timeStyle: "short",
                  },
                )}
              </div>
            </div>

            {!order.isFullDigital ? (
              <>
                <div className="my-2">
                  <Separator />
                </div>
                <div className="flex flex-col gap-3">
                  <div className="flex gap-1 text-sm font-medium">
                    <span>{t("ticket.order_detail.shipping")}</span>
                    {order.parcels.length > 1 ? (
                      <span className="text-muted-foreground">
                        {order.parcels.length}
                      </span>
                    ) : null}
                  </div>

                  <div>
                    <OrderParcels
                      isCancelled={order.isCancelled}
                      parcels={order.parcels}
                      shipping={order.shipping}
                    />
                    <div className="flex items-center">
                      <Copy
                        content={orderDeliveryMethod?.label ?? ""}
                        className="text-muted-foreground shrink-0 basis-[92px] truncate text-left text-sm"
                      >
                        {t("ticket.order_detail.delivery_method")}
                      </Copy>
                      <div className="flex min-h-8 items-center px-3">
                        {orderDeliveryMethod?.label ?? ""}
                      </div>
                    </div>
                    <div className="items-top flex">
                      <Copy
                        content={
                          (order.shipping.pickupPoint
                            ? pickupPointFormattedAddress
                                ?.filter((field) => field !== "")
                                .join("\n")
                            : recipientFormattedAddress
                                ?.filter((field) => field !== "")
                                .join("\n")) ?? ""
                        }
                        className="text-muted-foreground flex shrink-0 basis-[92px] items-stretch truncate py-1.5 text-left text-sm"
                      >
                        {t("ticket.order_detail.address")}
                      </Copy>
                      <div className="flex min-h-8 flex-col px-3 py-1.5">
                        {order.shipping.pickupPoint ? (
                          pickupPointFormattedAddress ? (
                            <AddressView
                              addressFields={pickupPointFormattedAddress}
                            />
                          ) : (
                            <AddressViewFallback
                              address={{
                                company:
                                  order.shipping.pickupPoint.address?.name,
                                street:
                                  order.shipping.pickupPoint.address?.street ??
                                  "",
                                streetAdditional:
                                  order.shipping.pickupPoint.address
                                    ?.streetAdditional,
                                city:
                                  order.shipping.pickupPoint.address?.city ??
                                  "",
                                country: "",
                                countryCode:
                                  order.shipping.pickupPoint.address
                                    ?.countryCode ?? "FR",
                                zipCode:
                                  order.shipping.pickupPoint.address?.zipCode ??
                                  "",
                              }}
                            />
                          )
                        ) : recipientFormattedAddress ? (
                          <AddressView
                            addressFields={recipientFormattedAddress}
                          />
                        ) : (
                          <AddressViewFallback
                            address={{
                              firstName: order.recipient.firstName,
                              lastName: order.recipient.lastName,
                              street: order.recipient.address.street,
                              streetAdditional:
                                order.recipient.address.streetAdditional,
                              city: order.recipient.address.city,
                              country: "",
                              countryCode: order.recipient.address.countryCode,
                              state: order.recipient.address.state,
                              zipCode: order.recipient.address.zipCode,
                            }}
                          />
                        )}
                      </div>
                    </div>
                    {order.shipping.pickupPoint?.openingDays ? (
                      <div className="items-top flex">
                        <span className="text-muted-foreground shrink-0 basis-[92px] truncate text-left text-sm">
                          {t("ticket.order_detail.opening_hours")}
                        </span>
                        <div className="flex min-h-8 w-full flex-col px-3">
                          <PickupPointOpeningHours
                            openingDays={order.shipping.pickupPoint.openingDays}
                          />
                        </div>
                      </div>
                    ) : null}
                  </div>
                </div>
              </>
            ) : null}
            {order.cashback ? (
              <>
                <div className="my-2">
                  <Separator />
                </div>
                <div className="flex flex-col gap-3">
                  <div className="flex gap-1 text-sm font-medium">
                    {t("ticket.order_detail.cashback.title")}
                  </div>

                  <div>
                    <div className="flex items-center">
                      <Copy
                        content={formatCurrency(
                          order.cashback.amount / 100,
                          toLocale(i18n.language),
                          {
                            currency: order.cashback.currency,
                          },
                        )}
                        className="text-muted-foreground shrink-0 basis-[92px] truncate text-left text-sm"
                      >
                        {t("ticket.order_detail.cashback.amount")}
                      </Copy>
                      <div className="flex min-h-8 items-center px-3">
                        {formatCurrency(
                          order.cashback.amount / 100,
                          toLocale(i18n.language),
                          { currency: order.cashback.currency },
                        )}
                      </div>
                    </div>
                    <div className="flex items-center">
                      <Copy
                        content={
                          getCashbackStatuses(t).find(
                            (status) => status.value === order.cashback?.status,
                          )?.label ?? ""
                        }
                        className="text-muted-foreground shrink-0 basis-[92px] truncate text-left text-sm"
                      >
                        {t("ticket.order_detail.cashback.status")}
                      </Copy>
                      <div className="flex min-h-8 items-center px-3">
                        <CashbackStatus
                          cashbackStatus={order.cashback.status}
                        />
                      </div>
                    </div>
                  </div>
                </div>
              </>
            ) : null}
            <div className="my-2">
              <Separator />
            </div>
            <div className="flex flex-col gap-3">
              <div className="flex items-center gap-1 text-sm">
                <span className="font-medium">
                  {t("ticket.order_detail.items")}
                </span>
                <span className="text-muted-foreground">
                  {order.items.length}
                </span>
              </div>

              <ul className="flex flex-col gap-3">
                {groupedItems.map((groupedItem) => (
                  <OrderItemWithPrice
                    item={groupedItem.item}
                    quantity={groupedItem.quantity}
                    key={groupedItem.key}
                  />
                ))}
              </ul>
            </div>
          </div>
        </div>
        <div className="bg-background w-full overflow-hidden rounded-lg border">
          <Tabs defaultValue="tickets" className="flex h-full grow flex-col">
            <TabsList className="mx-7 my-6">
              <TabsTrigger value="tickets">
                {t("order.tabs.tickets")}
              </TabsTrigger>
            </TabsList>
            <TabsContent
              value="tickets"
              className="h-full w-full overflow-auto"
            >
              <TicketsTab orderId={order.id} />
            </TabsContent>
          </Tabs>
        </div>
      </div>
      <Dialog open={isOpen} onOpenChange={setIsOpen}>
        <DialogContent>
          <CreateTicketDialog
            setIsOpen={setIsOpen}
            defaultContactId={order.supportActorAppContact.id}
            defaultOrderId={order.id}
          />
        </DialogContent>
      </Dialog>
    </>
  );
}

function TicketsTab({ orderId }: { orderId: string }) {
  const { t } = useTranslation();

  const {
    data,
    fetchNextPage,
    hasNextPage,
    isFetchingNextPage,
    isLoading,
    error,
  } = useInfiniteTicketsQuery(
    {
      filters: {
        contextDerived: {
          orderId: {
            in: [orderId],
          },
        },
      },
      sortBy: {
        field: SupportTicketsSortableFields.StatusUpdatedAt,
        direction: SupportTicketsSortableDirection.Desc,
      },
      first: 50,
    },
    {
      initialPageParam: undefined,
      getNextPageParam: (lastPage) => {
        return lastPage.supportTickets.pageInfo.hasNextPage
          ? {
              after: lastPage.supportTickets.pageInfo.endCursor,
            }
          : undefined;
      },
      select: (data) => data.pages.map((page) => page.supportTickets),
    },
  );

  const tickets =
    data?.flatMap((page) => page.edges).map((edge) => edge.node) ?? [];

  if (isLoading) {
    return (
      <div className="flex h-full w-full items-center justify-center">
        <Empty className="min-h-[300px] w-full max-w-md">
          <EmptyHeader>
            <EmptyMedia variant="icon">
              <Spinner className="size-4" />
            </EmptyMedia>
            <EmptyTitle>{t("tickets.loading")}</EmptyTitle>
          </EmptyHeader>
        </Empty>
      </div>
    );
  }

  if (error) {
    return (
      <div className="flex h-full w-full items-center justify-center">
        <Empty>
          <EmptyHeader>
            <EmptyMedia variant="icon">
              <ServerCrashIcon />
            </EmptyMedia>
            <EmptyTitle>{t("tickets.error")}</EmptyTitle>
          </EmptyHeader>
        </Empty>
      </div>
    );
  }

  if (tickets.length === 0) {
    return (
      <div className="flex h-full w-full items-center justify-center">
        <Empty>
          <EmptyHeader>
            <EmptyMedia variant="icon">
              <BookSearchIcon />
            </EmptyMedia>
            <EmptyTitle>{t("order.tickets.search.empty.title")}</EmptyTitle>
            <EmptyDescription>
              {t("order.tickets.search.empty.description")}
            </EmptyDescription>
          </EmptyHeader>
        </Empty>
      </div>
    );
  }

  return (
    <TicketList
      data={tickets}
      state={{
        columnVisibility: {
          ...DEFAULT_COLUMN_VISIBILITY,
          contact: false,
        },
        columnPinning: {
          start: ["select"],
          end: [],
        },
      }}
      onFetchMore={() => fetchNextPage()}
      isFetchingMore={isFetchingNextPage}
      hasMore={hasNextPage}
    />
  );
}
